import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import Action from '@sharedComponents/Action';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Tag from '@sharedComponents/Tag';
import Typography from '@sharedComponents/Typography';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { languageName, uiLocale } from './languageName';

import * as style from './style.module.css';

const FLAGS = {
    fr: style.flagFr,
    en: style.flagEn,
    de: style.flagDe,
    es: style.flagEs,
    it: style.flagIt,
    pt: style.flagPt,
    nl: style.flagNl,
    pl: style.flagPl,
    ro: style.flagRo,
    ua: style.flagUa,
    ar: style.flagAr,
};

// Share of the visible width scrolled by the chevrons, so that the last visible tab stays in view.
const SCROLL_RATIO = 0.8;

/**
 * Language tabs of a multilingual text, in a strip scrolled by chevrons at both ends. `languages` are
 * `{ value, label }` options whose label is the language code; each tab shows the flag and the name of
 * the language, the reference language carries a badge and a dot marks the languages left empty.
 */
const LanguageTabs = ({ languages = [], active = null, emptyLanguages = [], referenceLanguage = 'fr', onChange = () => {} }) => {
    const { t, i18n } = useTranslation('bo');
    const locale = uiLocale(i18n);
    const tabListRef = useRef(null);

    const scroll = (direction) => {
        const tabList = tabListRef.current;
        tabList?.scrollBy({ left: direction * tabList.clientWidth * SCROLL_RATIO, behavior: 'smooth' });
    };

    return (
        <FlexBox alignItems="center" className={style.strip}>
            <Button
                ghost
                icon={faChevronLeft}
                aria-label={t('Previous languages')}
                title={t('Previous languages')}
                action={() => scroll(-1)}
            />
            <FlexBox ref={tabListRef} role="tablist" aria-label={t('Languages')} flexGrow={1} flexShrink={1} className={style.tabs}>
                {languages.map(({ label }) => {
                    const selected = label === active;
                    return (
                        <Action
                            key={label}
                            role="tab"
                            tabIndex={0}
                            aria-selected={selected}
                            className={`${style.tab} ${selected ? style.selected : ''}`}
                            action={() => onChange(label)}
                        >
                            <FlexBox aria-hidden="true" className={`${style.flag} ${FLAGS[label] ?? style.flagNeutral}`} />
                            <Typography
                                span
                                fontSize="s"
                                fontWeight={selected ? 'semi-bold' : 'regular'}
                                color={selected ? 'branded-primary' : 'neutral-secondary'}
                            >
                                {languageName(label, locale)}
                            </Typography>
                            {label === referenceLanguage && <Tag color="lavender">{t('Reference')}</Tag>}
                            {emptyLanguages.includes(label) && (
                                <FlexBox
                                    role="img"
                                    aria-label={t('Empty translation')}
                                    title={t('Empty translation')}
                                    className={style.emptyDot}
                                />
                            )}
                        </Action>
                    );
                })}
            </FlexBox>
            <Button ghost icon={faChevronRight} aria-label={t('Next languages')} title={t('Next languages')} action={() => scroll(1)} />
        </FlexBox>
    );
};

export default LanguageTabs;
